import React from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    makeStyles,
    Divider,
    IconButton,
} from '@material-ui/core';
import { InsertInvitation } from '@material-ui/icons';
import { CheckMarkGreen, CrossMarkIcon, AadhaarIcon } from '../../assets/svg';
import { LightBoxImage } from '../UI';
import FileUploaderTyped from '../UI/FileUploader/FileUploaderTyped';
import { CustomTextField } from '../Textfield';
import { CustomDatePicker } from '../DateTimePicker';
import {
    isDigilockerVerified,
    verifiedByDigilockerStatus,
    getMaxDOBFor18Years,
    isImageLink,
    isBlueDartOrg,
    isDocActionBlockedForRole,
    stripDocumentNumberSpaces,
} from '../../utils/appHelper';
import {
    APPROVE_STATUS,
    DOCUMENT_REJECTED_STATUS,
} from '../../utils/appConstants';
import moment from 'moment';
import { PdfViewerButton } from '../Modals/PdfViewerModalV2';
import { is } from 'date-fns/locale';
import {
    ImageProcessingNotice,
    RevealButton,
    useRevealedImage,
    useRevealedNumber,
} from './reveal';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type DocumentType =
    | 'AADHAAR'
    | 'PAN'
    | 'DL'
    | 'RC'
    | 'EMIRATES_ID'
    | 'EMIRATES_DL'
    | 'INSURANCE_CARD'
    | 'VISA_COPY'
    | 'PASSPORT'
    | 'PCC_APPLIED_COPY'
    | 'PCC_FINAL_CLEARANCE';
export type ApprovalStatus =
    | 'APPROVED'
    | 'REJECTED'
    | 'PENDING'
    | 'PENDING_BD_OPS_APPROVAL'
    | 'PENDING_VENDOR_APPROVAL'
    | null;

export interface KYCDocumentPayload {
    id?: number | null;
    documentNo?: string | null;
    image?: string | null;
    imageFront?: string | null;
    imageBack?: string | null;
    approvalStatus?: ApprovalStatus | string;
    rejectReason?: string | null;
    autoKycBroker?: string | null;
    autoKycDocumentNo?: string | null;
    autoKycDocumentName?: string | null;
    autoKycDocumentDob?: string | null;
    faceMatchConfidence?: string | null;
    faceMatchScore?: string | null;
    verified?: boolean;
    docDate?: string | null;
    docDateMilliSeconds?: number | null;
    fatherName?: string | null;
    dlCaseCheck?: string | null;
    verificationType?: string | null;
    profileImageLink?: string | null;
    /** Server-computed: false => render no reveal control at all, not a disabled one. */
    revealable?: boolean | null;
    imageRevealable?: boolean | null;
    /** True when the blurred variant does not exist yet; never fall back to the raw image. */
    imageUnavailable?: boolean | null;
    imageUnmasked?: boolean | null;
    docNumberMismatch?: boolean | null;
    pdfDocumentLink?: string | null;
}

export interface UidaiAadharInfo {
    kycPhotoS3Url?: string;
    name?: string;
    dateOfBirth?: string;
    address?: string;
}

export interface EditableKYCDocumentCardProps {
    title: string;
    documentType: DocumentType;
    document: KYCDocumentPayload | null;
    isEdited: boolean;
    isAutoVerified: boolean;
    isKycRequired?: boolean;
    showBackImage?: boolean;
    showDOBField?: boolean;
    showTextInput?: boolean;
    enableEdit?: boolean;
    dobEnableEdit?: boolean;
    showFatherNameField?: boolean;
    fatherNameEnableEdit?: boolean;
    onFatherNameSave?: () => void;
    isPendingAtOps?: boolean;
    uidaiInfo?: UidaiAadharInfo | null;
    uidaiKycRequired?: boolean;
    uidaiKycVerified?: boolean;
    onDocumentChange: (field: string, value: any) => void;
    onSave: () => void;
    onApprove: () => void;
    onReject: () => void;
    onInitiate?: () => void;
    onEditStateChange: (edited: boolean) => void;
    disabled?: boolean;
    isFailed?: boolean;
    showActions?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles((theme) => ({
    card: {
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '12px',
        border: '1px solid rgba(38, 50, 56, 0.16)',
        backgroundColor: '#fff',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    title: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#14112d',
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    statusText: {
        fontSize: '18px',
        fontWeight: 700,
    },
    statusApproved: {
        color: '#4caf50',
    },
    statusRejected: {
        color: '#f44336',
    },
    statusPending: {
        color: '#ff9800',
    },
    dividerLine: {
        flex: 1,
        marginLeft: '12px',
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    fieldLabel: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#14112d',
        minWidth: 'fit-content',
    },
    textField: {
        width: '30%',
        minWidth: '200px',
        marginBottom: 0,
    },
    actionBtn: {
        marginLeft: '12px',
    },
    saveBtn: {
        marginLeft: '16px',
        marginRight: '16px',
    },
    approveBtn: {
        marginRight: '16px',
        fontSize: 14,
        color: '#4caf50',
        border: '1px solid #4caf50',
        fontWeight: 700,
        '&:disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
        },
    },
    rejectBtn: {
        marginRight: '16px',
        backgroundColor: '#fff',
        border: '1px solid #f44336',
        color: '#f44336',
        fontWeight: 600,
        '&:disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            border: '1px solid rgba(0, 0, 0, 0.12)',
        },
    },
    imageGrid: {
        marginTop: '16px',
    },
    faceMatchContainer: {
        display: 'flex',
        alignItems: 'center',
        margin: '12px 0',
    },
    profileImageContainer: {
        display: 'flex',
        alignItems: 'center',
        margin: '12px 0',
    },
    faceMatchLabel: {
        fontFamily: 'Nunito',
        fontSize: '16px',
        fontWeight: 600,
        color: '#14112d',
        marginRight: '8px',
    },
    rejectReason: {
        marginTop: '12px',
        padding: '12px',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
    },
    rejectReasonLabel: {
        fontSize: '14px',
        fontWeight: 500,
        marginBottom: '4px',
    },
    rejectReasonText: {
        color: '#f44336',
        fontSize: '14px',
        fontWeight: 600,
    },
    noDocumentContainer: {
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px dashed rgba(38, 50, 56, 0.24)',
    },
    noDocumentText: {
        color: '#9e9e9e',
        fontSize: '14px',
        fontStyle: 'italic',
    },
    uidaiInfoBox: {
        border: 'solid 1px rgba(38, 50, 56, 0.32)',
        borderRadius: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        height: '160px',
        padding: 8,
        paddingRight: 0,
        overflowY: 'auto',
    },
    uidaiPendingBox: {
        border: 'solid 1px rgba(38, 50, 56, 0.32)',
        borderRadius: 16,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        height: '160px',
        padding: 8,
    },
    uidaiLabel: {
        fontSize: '14px',
        fontWeight: 400,
        color: 'rgba(20, 17, 45, 0.64)',
    },
    uidaiBold: {
        color: '#14112d',
        fontWeight: 600,
    },
    errorText: {
        marginTop: '8px',
        color: '#f44336',
        fontWeight: 600,
    },
}));

// ============================================================================
// Helper Functions
// ============================================================================

interface StatusInfo {
    text: string;
    status:
        | 'APPROVED'
        | 'REJECTED'
        | 'PENDING'
        | 'DIGILOCKER'
        | 'NOT_SUBMITTED'
        | 'DL_VERIFIED'
        | 'DL_FAILED';
    canTakeAction?: boolean;
}

const getStatusInfo = (
    document: KYCDocumentPayload | null,
    isAutoVerified: boolean,
    documentType: DocumentType,
    isPendingAtOps?: boolean,
): StatusInfo => {
    if (!document || !document?.id) {
        return { text: 'Not Submitted', status: 'NOT_SUBMITTED' };
    }

    const isVerifiedByDigilocker = isDigilockerVerified(document);
    const isApproved = document?.approvalStatus === 'APPROVED';
    const isRejected = DOCUMENT_REJECTED_STATUS.includes(
        document?.approvalStatus || '',
    );
    const isPending = APPROVE_STATUS.includes(document?.approvalStatus || '');

    // DL/PAN specific status
    if (documentType === 'DL' || documentType === 'PAN') {
        const dlCaseCheck = document?.dlCaseCheck;
        if (dlCaseCheck === 'VERIFIED') {
            return { text: 'Verified', status: 'DL_VERIFIED' };
        }
        if (dlCaseCheck === 'INITIATION_FAILED') {
            return { text: 'Initiation Failed', status: 'DL_FAILED' };
        }
        if (
            dlCaseCheck &&
            !['VERIFIED', 'INITIATION_FAILED'].includes(dlCaseCheck)
        ) {
            return { text: dlCaseCheck.toLowerCase(), status: 'PENDING' };
        }
    }

    if (isVerifiedByDigilocker) {
        return { text: 'Verified by DigiLocker', status: 'DIGILOCKER' };
    }

    if (isAutoVerified) {
        return { text: 'Verified', status: 'APPROVED' };
    }

    if (isApproved) {
        return { text: 'Manually Verified', status: 'APPROVED' };
    }

    if (isRejected) {
        const isAutoRejectedByOcr =
            document?.verificationType === 'Auto Rejected by OCR';
        return {
            text:
                document?.approvalStatus === 'DUPLICATE_REJECTED'
                    ? 'Duplicate Rejected'
                    : isAutoRejectedByOcr
                      ? 'Auto Rejected'
                      : 'Manually Rejected',
            status: 'REJECTED',
        };
    }

    return {
        text:
            !!isBlueDartOrg() && isPendingAtOps
                ? 'Pending Verification at OPS'
                : 'Pending Verification',
        status: 'PENDING',
        canTakeAction: isPending,
    };
};

// ============================================================================
// Sub-Components
// ============================================================================

interface StatusBadgeProps {
    statusInfo: StatusInfo;
    classes: ReturnType<typeof useStyles>;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ statusInfo, classes }) => {
    const getStatusClass = () => {
        switch (statusInfo.status) {
            case 'APPROVED':
            case 'DIGILOCKER':
            case 'DL_VERIFIED':
                return classes.statusApproved;
            case 'REJECTED':
            case 'DL_FAILED':
                return classes.statusRejected;
            default:
                return classes.statusPending;
        }
    };

    const getStatusIcon = () => {
        switch (statusInfo.status) {
            case 'APPROVED':
            case 'DIGILOCKER':
            case 'DL_VERIFIED':
                return <CheckMarkGreen style={{ width: '20px' }} />;
            case 'REJECTED':
            case 'DL_FAILED':
                return <CrossMarkIcon style={{ width: '20px' }} />;
            default:
                return null;
        }
    };

    if (statusInfo.status === 'NOT_SUBMITTED') return null;

    return (
        <Box className={classes.statusContainer}>
            {getStatusIcon()}
            <Typography className={`${classes.statusText} ${getStatusClass()}`}>
                {statusInfo.text}
            </Typography>
        </Box>
    );
};

interface FaceMatchDisplayProps {
    confidence: string | null | undefined;
    classes: ReturnType<typeof useStyles>;
}
interface ProfileImageDisplayProps {
    image: string | null | undefined;
    classes: ReturnType<typeof useStyles>;
}

const FaceMatchDisplay: React.FC<FaceMatchDisplayProps> = ({
    confidence,
    classes,
}) => {
    if (!confidence) return null;

    const confidenceValue = Number(confidence);
    const color = confidenceValue > 80 ? '#4caf50' : '#f44336';

    return (
        <Box className={classes.faceMatchContainer}>
            <Typography className={classes.faceMatchLabel}>
                Face match:
            </Typography>
            <Typography style={{ color, fontWeight: 600 }}>
                {confidence}
            </Typography>
        </Box>
    );
};
const ProfileImageDisplay: React.FC<ProfileImageDisplayProps> = ({
    image,
    classes,
}) => {
    if (!image) return null;

    return (
        <Box className={classes.profileImageContainer}>
            {!!image && (
                <LightBoxImage
                    source={image}
                    containerStyles={{
                        width: '20%',
                        borderRadius: 10,
                        border: 'solid 1px rgba(38, 50, 56, 0.32)',
                    }}
                    imageStyles={{
                        objectFit: 'fill',
                        borderRadius: 10,
                        margin: 0,
                        height: '100%',
                    }}
                />
            )}
        </Box>
    );
};

interface UidaiInfoSectionProps {
    uidaiInfo: UidaiAadharInfo | null | undefined;
    uidaiKycVerified: boolean;
    classes: ReturnType<typeof useStyles>;
}

const UidaiInfoSection: React.FC<UidaiInfoSectionProps> = ({
    uidaiInfo,
    uidaiKycVerified,
    classes,
}) => {
    if (uidaiKycVerified && uidaiInfo) {
        return (
            <Grid item sm={4} xs={12}>
                <Typography
                    variant="body1"
                    style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        marginTop: 10,
                        marginBottom: 8,
                        color: '#14112d',
                    }}
                >
                    Aadhaar User Info
                </Typography>
                <Box className={classes.uidaiInfoBox}>
                    <LightBoxImage
                        source={uidaiInfo.kycPhotoS3Url}
                        containerStyles={{
                            width: '40%',
                            borderRadius: 10,
                            border: 'solid 1px rgba(38, 50, 56, 0.32)',
                        }}
                        imageStyles={{
                            objectFit: 'fill',
                            borderRadius: 10,
                            margin: 0,
                            height: '100%',
                        }}
                    />
                    <Box style={{ width: '60%', marginLeft: 12 }}>
                        <Typography className={classes.uidaiLabel}>
                            Name:{' '}
                            <b className={classes.uidaiBold}>
                                {uidaiInfo.name}
                            </b>
                        </Typography>
                        <Typography className={classes.uidaiLabel}>
                            DOB:{' '}
                            <b className={classes.uidaiBold}>
                                {uidaiInfo.dateOfBirth}
                            </b>
                        </Typography>
                        <Typography className={classes.uidaiLabel}>
                            Address: <br />
                            <b className={classes.uidaiBold}>
                                {uidaiInfo.address}
                            </b>
                        </Typography>
                    </Box>
                </Box>
            </Grid>
        );
    }

    return (
        <Grid item sm={4} xs={12}>
            <Typography
                variant="body1"
                style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginTop: 10,
                    marginBottom: 8,
                    color: '#14112d',
                }}
            >
                Aadhaar User Info
            </Typography>
            <Box className={classes.uidaiPendingBox}>
                <AadhaarIcon style={{ width: 100 }} />
                <Typography
                    style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        textAlign: 'center',
                    }}
                >
                    UIDAI Verification is pending
                </Typography>
                <Typography
                    style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        textAlign: 'center',
                    }}
                >
                    Please Ask the Employee to Complete Verification
                </Typography>
            </Box>
        </Grid>
    );
};

// ============================================================================
// Document Not Provided Card Component
// ============================================================================

export interface KYCDocumentNotProvidedCardProps {
    title: string;
    documentType: DocumentType;
}

const useNotProvidedStyles = makeStyles(() => ({
    card: {
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '12px',
        border: '1px solid rgba(38, 50, 56, 0.16)',
        backgroundColor: '#fff',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    title: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#14112d',
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginLeft: '12px',
    },
    statusText: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#9e9e9e',
    },
    dividerLine: {
        flex: 1,
        marginLeft: '12px',
    },
    notProvidedContainer: {
        padding: '32px 40px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px dashed rgba(38, 50, 56, 0.24)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
    },
    notProvidedIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '8px',
    },
    notProvidedTitle: {
        color: '#616161',
        fontSize: '16px',
        fontWeight: 600,
    },
    notProvidedText: {
        color: '#9e9e9e',
        fontSize: '14px',
    },
}));

export const KYCDocumentNotProvidedCard: React.FC<
    KYCDocumentNotProvidedCardProps
> = ({ title, documentType }) => {
    const classes = useNotProvidedStyles();

    const getDocumentLabel = (): string => {
        switch (documentType) {
            case 'AADHAAR':
                return 'Aadhaar Card';
            case 'PAN':
                return 'PAN Card';
            case 'DL':
                return 'Driving License';
            case 'RC':
                return 'Vehicle RC';
            default:
                return title;
        }
    };

    return (
        <Box className={classes.card}>
            {/* Header */}
            <Box className={classes.cardHeader}>
                <Box className={classes.titleContainer}>
                    <Typography className={classes.title}>{title}</Typography>
                    <Box className={classes.statusContainer}>
                        <CrossMarkIcon
                            style={{ width: '18px', opacity: 0.5 }}
                        />
                        <Typography className={classes.statusText}>
                            Not Provided
                        </Typography>
                    </Box>
                </Box>
                <Divider className={classes.dividerLine} />
            </Box>

            {/* Not Provided Content */}
            <Box className={classes.notProvidedContainer}>
                <Box className={classes.notProvidedIcon}>
                    <CrossMarkIcon style={{ width: '24px', opacity: 0.4 }} />
                </Box>
                <Typography className={classes.notProvidedTitle}>
                    {getDocumentLabel()} Not Uploaded
                </Typography>
                <Typography className={classes.notProvidedText}>
                    The employee has not uploaded their{' '}
                    {getDocumentLabel().toLowerCase()} yet
                </Typography>
            </Box>
        </Box>
    );
};

// ============================================================================
// Main Component
// ============================================================================

const EditableKYCDocumentCard: React.FC<EditableKYCDocumentCardProps> = ({
    title,
    documentType,
    document,
    isEdited,
    isAutoVerified,
    isKycRequired = true,
    showBackImage = true,
    showDOBField = false,
    showTextInput = true,
    enableEdit = true,
    dobEnableEdit,
    showFatherNameField = false,
    fatherNameEnableEdit,
    onFatherNameSave,
    isPendingAtOps = false,
    uidaiInfo,
    uidaiKycRequired = false,
    uidaiKycVerified = false,
    onDocumentChange,
    onSave,
    onApprove,
    onReject,
    onInitiate,
    onEditStateChange,
    disabled = false,
    isFailed = false,
    showActions = true,
}) => {
    const classes = useStyles();
    const isVerifiedByDigilocker = document
        ? isDigilockerVerified(document)
        : false;
    const digilockerInfo = document
        ? verifiedByDigilockerStatus(document)
        : null;
    const statusInfo = getStatusInfo(
        document,
        isAutoVerified,
        documentType,
        isPendingAtOps,
    );

    const isSingleImageDocType =
        documentType === 'PAN' ||
        documentType === 'PCC_APPLIED_COPY' ||
        documentType === 'PCC_FINAL_CLEARANCE';
    const servedFrontImage = isSingleImageDocType
        ? document?.image
        : document?.imageFront || document?.image;

    const revealedNumber = useRevealedNumber(document?.id);
    // A live UNBLUR_IMAGE grant swaps in the raw object; otherwise this is whatever the API
    // served, which is the blurred variant (or null when it has not been produced yet).
    const frontImageUrl = useRevealedImage(
        document?.id,
        servedFrontImage,
        'FRONT',
    );
    const backImageUrl = useRevealedImage(
        document?.id,
        document?.imageBack,
        'BACK',
    );
    const isDocumentVerified =
        statusInfo.status === 'APPROVED' ||
        statusInfo.status === 'DIGILOCKER' ||
        statusInfo.status === 'DL_VERIFIED';
    const hasDocument = !!document?.id;
    const isInputDisabled =
        disabled ||
        isVerifiedByDigilocker ||
        isAutoVerified ||
        !enableEdit ||
        isDocumentVerified;
    const canTakeAction =
        !isPendingAtOps &&
        !isVerifiedByDigilocker &&
        !isAutoVerified &&
        enableEdit;
    const isOtherDocumentFound = !!document?.pdfDocumentLink;

    const getFieldLabel = (): string => {
        if (isVerifiedByDigilocker) {
            return `${title} Number`;
        }
        return `Enter ${title} Number *`;
    };

    const handleFieldChange = (field: string, value: any) => {
        onDocumentChange(field, value);
        onEditStateChange(true);
    };
    const isInitiateDisabled = (): boolean => {
        if (documentType !== 'DL' && documentType !== 'PAN') return true;
        // Initiate allowed only when dlCaseCheck is null/undefined or previously failed
        const dlCase = document?.dlCaseCheck;
        return !(
            dlCase === null ||
            dlCase === undefined ||
            dlCase === 'INITIATION_FAILED'
        );
    };

    const handleImageUpload = (field: string, url: string) => {
        handleFieldChange(field, url);
    };

    const handleImageRemove = (field: string) => {
        handleFieldChange(field, null);
    };

    const isSaveDisabled = (): boolean => {
        if (isVerifiedByDigilocker || isAutoVerified) return true;
        if (!isEdited) return true;
        if (showTextInput && !document?.documentNo?.trim()) return true;

        // Aadhaar requires both front and back
        if (documentType === 'AADHAAR') {
            return !document?.imageFront || !document?.imageBack;
        }

        // PAN requires front image (uses 'image' field)
        if (documentType === 'PAN') {
            return !document?.image;
        }

        // RC requires front image (can use either image or imageFront)
        if (documentType === 'RC') {
            return !(document?.image || document?.imageFront);
        }

        // DL - special validation
        if (documentType === 'DL') {
            return !enableEdit;
        }

        return false;
    };

    const hasAnyImage =
        !!document?.imageFront ||
        !!document?.imageBack ||
        !!document?.image ||
        !!document?.profileImageLink ||
        !!document?.pdfDocumentLink;

    // Block the role that owns the other approval stage (vendor vs bd-ops).
    const isActionBlockedForRole = isDocActionBlockedForRole(
        document?.approvalStatus,
    );

    const isApproveDisabled = (): boolean => {
        return (
            !hasAnyImage ||
            document?.dlCaseCheck === 'INITIATED' ||
            isVerifiedByDigilocker ||
            isAutoVerified ||
            isEdited ||
            isActionBlockedForRole ||
            document?.approvalStatus === 'APPROVED'
        );
    };

    const isRejectDisabled = (): boolean => {
        return (
            !hasAnyImage ||
            isVerifiedByDigilocker ||
            isAutoVerified ||
            isDocumentVerified ||
            isEdited ||
            isActionBlockedForRole ||
            !!isPendingAtOps
        );
    };

    // Render for documents that have not been submitted
    if (!hasDocument && !isKycRequired) {
        return (
            <Box className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Box className={classes.titleContainer}>
                        <Typography className={classes.title}>
                            {title}
                        </Typography>
                        <Divider className={classes.dividerLine} />
                    </Box>
                </Box>
                <Box className={classes.noDocumentContainer}>
                    <Typography className={classes.noDocumentText}>
                        No {title.toLowerCase()} information submitted
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.card}>
            {/* Header */}
            <Box className={classes.cardHeader}>
                <Box className={classes.titleContainer}>
                    <Typography className={classes.title}>{title}</Typography>
                    {showActions && (
                        <StatusBadge
                            statusInfo={statusInfo}
                            classes={classes}
                        />
                    )}
                </Box>
                <Divider className={classes.dividerLine} />
            </Box>

            {/* Document Number Input Row */}

            {/* DL specific DOB field */}
            {/* Row 1: Document Number + Action Buttons */}
            <Box className={classes.fieldRow} style={{ marginBottom: '16px' }}>
                {showTextInput && (
                    <>
                        <Typography className={classes.fieldLabel}>
                            {getFieldLabel()}
                        </Typography>
                        <CustomTextField
                            value={revealedNumber || document?.documentNo || ''}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                                handleFieldChange(
                                    'documentNo',
                                    stripDocumentNumberSpaces(e.target.value),
                                )
                            }
                            variant="outlined"
                            className={classes.textField}
                            disabled={isInputDisabled}
                        />
                        <RevealButton
                            documentId={document?.id}
                            documentType={documentType}
                            revealable={document?.revealable}
                            action="UNMASK_NUMBER"
                            subject={`${title} number`}
                        />
                    </>
                )}

                {/* Primary Action Buttons (Always Visible) */}
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.saveBtn}
                    style={{ marginLeft: 0 }}
                    size="large"
                    disabled={isSaveDisabled()}
                    disableElevation
                    onClick={onSave}
                >
                    {documentType === 'DL' || documentType === 'PAN'
                        ? 'Update'
                        : 'Save'}
                </Button>
                {showActions && (
                    <Button
                        variant="outlined"
                        className={classes.approveBtn}
                        style={{ marginLeft: 0 }}
                        disabled={isApproveDisabled()}
                        disableElevation
                        onClick={onApprove}
                    >
                        Approve
                    </Button>
                )}
                {showActions && (
                    <Button
                        variant="outlined"
                        className={classes.rejectBtn}
                        style={{ marginLeft: 0 }}
                        disabled={isRejectDisabled()}
                        disableElevation
                        onClick={onReject}
                    >
                        Reject
                    </Button>
                )}
                {(documentType === 'DL' || documentType === 'PAN') &&
                    onInitiate && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.saveBtn}
                            style={{ marginLeft: 0 }}
                            size="large"
                            disabled={isInitiateDisabled()}
                            disableElevation
                            onClick={onInitiate}
                        >
                            Initiate
                        </Button>
                    )}
            </Box>

            {/* Row 2: DL specific DOB field row */}
            {showDOBField && (
                <Box
                    className={classes.fieldRow}
                    style={{ marginBottom: '16px' }}
                >
                    <Typography className={classes.fieldLabel}>
                        Enter DOB *
                    </Typography>
                    <CustomDatePicker
                        label="Date of Birth"
                        id="dob"
                        name="dob"
                        value={document?.docDateMilliSeconds}
                        showTodayButton={false}
                        allowAge18={false}
                        maximumDate={getMaxDOBFor18Years()}
                        onChange={(value: number) =>
                            handleFieldChange('docDateMilliSeconds', value)
                        }
                        error={false}
                        helperText={null}
                        showInput={true}
                        allowFutureDates={false}
                        InputProps={{
                            endAdornment: (
                                <IconButton style={{ padding: 0 }}>
                                    <InsertInvitation />
                                </IconButton>
                            ),
                        }}
                        style={{
                            width: '30%',
                            minWidth: '200px',
                            marginBottom: 0,
                        }}
                        disabled={
                            dobEnableEdit !== undefined
                                ? !dobEnableEdit
                                : !enableEdit
                        }
                    />
                </Box>
            )}
            {/* Father's Name field (BD Aadhaar): shown right after DOB */}
            {showFatherNameField && (
                <Box
                    className={classes.fieldRow}
                    style={{ marginBottom: '16px' }}
                >
                    <Typography className={classes.fieldLabel}>
                        Father's Name
                    </Typography>
                    <CustomTextField
                        value={document?.fatherName || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleFieldChange('fatherName', e.target.value)
                        }
                        variant="outlined"
                        className={classes.textField}
                        disabled={
                            fatherNameEnableEdit !== undefined
                                ? !fatherNameEnableEdit
                                : !enableEdit
                        }
                    />
                    {onFatherNameSave && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.saveBtn}
                            size="large"
                            disableElevation
                            disabled={
                                (fatherNameEnableEdit !== undefined
                                    ? !fatherNameEnableEdit
                                    : !enableEdit) ||
                                !document?.fatherName?.trim()
                            }
                            onClick={onFatherNameSave}
                        >
                            Save Father's Name
                        </Button>
                    )}
                </Box>
            )}
            {/* Profile Image */}
            <ProfileImageDisplay
                image={document?.profileImageLink}
                classes={classes}
            />
            {!!isOtherDocumentFound && (
                <Box style={{ marginTop: '12px' }}>
                    <PdfViewerButton
                        file={document?.pdfDocumentLink}
                        variant="link"
                        linkText="Click to view PDF document from DigiLocker"
                    />
                </Box>
            )}
            {/* Face Match Score */}
            <FaceMatchDisplay
                confidence={
                    isBlueDartOrg()
                        ? Number(document?.faceMatchScore) > 0
                            ? document?.faceMatchScore
                            : null
                        : document?.faceMatchConfidence
                }
                classes={classes}
            />

            {/* Document Images */}
            {!isVerifiedByDigilocker && document?.imageUnavailable && (
                <ImageProcessingNotice />
            )}
            {!isVerifiedByDigilocker && (
                <Box style={{ marginBottom: 8 }}>
                    <RevealButton
                        documentId={document?.id}
                        documentType={documentType}
                        revealable={document?.imageRevealable}
                        action="UNBLUR_IMAGE"
                        documentKeys={
                            document?.imageBack ? ['FRONT', 'BACK'] : undefined
                        }
                        subject={`${title} image`}
                    />
                </Box>
            )}
            {!isVerifiedByDigilocker && (
                <Grid container spacing={2} className={classes.imageGrid}>
                    {/* Front Image */}
                    <Grid item sm={showBackImage ? 6 : 12} xs={12}>
                        <FileUploaderTyped
                            label={
                                documentType === 'PAN'
                                    ? 'Upload Pan card (front)'
                                    : `Upload ${title}`
                            }
                            labelStyles={{ fontSize: '16px', fontWeight: 600 }}
                            placeholder={`Upload ${title}`}
                            disabled={
                                isAutoVerified ||
                                !enableEdit ||
                                isDocumentVerified
                            }
                            defaultValue={frontImageUrl}
                            flowType="KYC"
                            onFileUploaded={(url: string) =>
                                handleImageUpload(
                                    documentType === 'PAN' ||
                                        documentType === 'PCC_APPLIED_COPY' ||
                                        documentType === 'PCC_FINAL_CLEARANCE'
                                        ? 'image'
                                        : 'imageFront',
                                    url,
                                )
                            }
                            onRemoved={() =>
                                handleImageRemove(
                                    documentType === 'PAN' ||
                                        documentType === 'PCC_APPLIED_COPY' ||
                                        documentType === 'PCC_FINAL_CLEARANCE'
                                        ? 'image'
                                        : 'imageFront',
                                )
                            }
                        />
                    </Grid>

                    {/* Back Image */}
                    {showBackImage && (
                        <Grid item sm={6} xs={12}>
                            <FileUploaderTyped
                                label={`Upload ${title} (Back)`}
                                labelStyles={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                }}
                                placeholder={`Upload ${title} (Back)`}
                                disabled={
                                    isAutoVerified ||
                                    !enableEdit ||
                                    isDocumentVerified
                                }
                                defaultValue={backImageUrl}
                                flowType="KYC"
                                onFileUploaded={(url: string) =>
                                    handleImageUpload('imageBack', url)
                                }
                                onRemoved={() => handleImageRemove('imageBack')}
                            />
                        </Grid>
                    )}

                    {/* UIDAI Info Section for Aadhaar */}
                    {documentType === 'AADHAAR' && uidaiKycRequired && (
                        <UidaiInfoSection
                            uidaiInfo={uidaiInfo}
                            uidaiKycVerified={uidaiKycVerified}
                            classes={classes}
                        />
                    )}
                </Grid>
            )}

            {/* Digilocker verified info */}
            {!!digilockerInfo?.isDigilockerBroker && digilockerInfo?.name && (
                <Box
                    style={{
                        padding: '12px',
                        backgroundColor: '#e8f5e9',
                        borderRadius: '8px',
                        marginTop: '12px',
                    }}
                >
                    <Grid container spacing={2}>
                        {digilockerInfo?.name && (
                            <Grid item xs={6}>
                                <Typography
                                    style={{
                                        fontSize: '13px',
                                        color: 'rgba(20, 17, 45, 0.64)',
                                    }}
                                >
                                    Name (as per DigiLocker)
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#14112d',
                                    }}
                                >
                                    {digilockerInfo.name}
                                </Typography>
                            </Grid>
                        )}
                        {digilockerInfo?.documentDob && (
                            <Grid item xs={6}>
                                <Typography
                                    style={{
                                        fontSize: '13px',
                                        color: 'rgba(20, 17, 45, 0.64)',
                                    }}
                                >
                                    Date of Birth
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#14112d',
                                    }}
                                >
                                    {digilockerInfo.documentDob}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* Reject Reason */}
            {!isDocumentVerified && document?.rejectReason && (
                <Box className={classes.rejectReason}>
                    <Typography className={classes.rejectReasonLabel}>
                        Reject Reason
                    </Typography>
                    <Typography className={classes.rejectReasonText}>
                        {document.rejectReason}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default EditableKYCDocumentCard;
